import { Controller } from "@hotwired/stimulus";
import escapeAttr from "./admin/massimo/escape_attr";

// The server renders each asyncDropdown target with just its current selection.
// The full option list is fetched from `/dropdown` the first time the popover opens.
export default class extends Controller {
  static targets = ["asyncDropdown"];

  connect() {
    this.loadedDropdowns = new Set();

    this.asyncDropdownTargets.forEach((dropdown) => {
      this.setupDropdown(dropdown);
    });
  }

  asyncDropdownTargetConnected(dropdown) {
    if (!this.loadedDropdowns) return;
    this.setupDropdown(dropdown);
  }

  setupDropdown(dropdown) {
    const trigger = dropdown.querySelector('[data-select-target="trigger"]');
    if (!trigger || trigger.dataset.ticketFiltersBound === "true") return;

    trigger.dataset.ticketFiltersBound = "true";
    const handler = () => this.handleTriggerActivation(dropdown);
    trigger.addEventListener("click", handler);
    trigger.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") {
        handler();
      }
    });
  }

  handleTriggerActivation(dropdown) {
    const dropdownType = dropdown.dataset.dropdownType;
    if (!dropdownType) return;
    if (this.loadedDropdowns.has(dropdownType)) return;
    this.loadDropdown(dropdown, dropdownType);
  }

  async loadDropdown(dropdown, dropdownType) {
    if (this.loadedDropdowns.has(dropdownType) || dropdown.dataset.loading === "true") return;

    dropdown.dataset.loading = "true";
    this.showLoadingItem(dropdown);

    try {
      const data = await this.fetchOptions(dropdownType);
      this.populateDropdown(dropdown, data.options || []);
      this.loadedDropdowns.add(dropdownType);
    } catch (error) {
      console.error(`Error loading dropdown ${dropdownType}:`, error);
      this.removeLoadingItem(dropdown);
    } finally {
      dropdown.dataset.loading = "false";
    }
  }

  async fetchOptions(dropdownType) {
    const urlParams = new URLSearchParams(window.location.search);
    const dropdownPath =
      this.element.dataset.dropdownUrl || `${window.location.pathname.replace(/\/$/, "")}/dropdown`;
    const url = `${dropdownPath}?${urlParams.toString()}&dropdown_type=${dropdownType}`;
    const response = await fetch(url, {
      headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    return response.json();
  }

  showLoadingItem(dropdown) {
    const group = dropdown.querySelector('[role="group"]');
    if (!group) return;
    const loading = document.createElement("div");
    loading.dataset.ticketFiltersLoading = "true";
    loading.className = "px-2 py-1.5 text-sm text-muted-foreground";
    loading.textContent = "Loading…";
    group.appendChild(loading);
  }

  removeLoadingItem(dropdown) {
    const loading = dropdown.querySelector('[data-ticket-filters-loading="true"]');
    if (loading) loading.remove();
  }

  populateDropdown(dropdown, options) {
    const group = dropdown.querySelector('[role="group"]');
    if (!group) return;
    const selectedValue = dropdown.querySelector('[data-select-target="hiddenInput"]')?.value || "";
    group.innerHTML = options.map((opt) => this.optionMarkup(opt, selectedValue)).join("");
  }

  optionMarkup(option, selectedValue) {
    const value = String(option.value ?? "");
    const text = option.text ?? "";
    const isSelected = option.selected === true || value === selectedValue;
    const escapedValue = escapeAttr(value);
    const escapedText = escapeAttr(text);
    const check = isSelected
      ? '<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">' +
        '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>'
      : "";
    return (
      '<div data-ui--filter-target="item">' +
      '<div class="flex items-center space-x-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer ' +
      'hover:bg-accent hover:text-accent-foreground" ' +
      'data-select-target="option" ' +
      `data-select-value="${escapedValue}" ` +
      'data-action="click->select#selectOption">' +
      `<div class="w-4 h-4 text-brand-500">${check}</div>` +
      `<span class="flex-1">${escapedText}</span>` +
      "</div></div>"
    );
  }
}
